import { isArrayEmpty, isNumeric, isObjectNull } from "../../../helpers/ObjectHelpers";
import { getNumericUrlParam } from "../../../helpers/UrlHelpers";
import { wktToGeoJSON } from "@terraformer/wkt"
import { MapPropertiesProps } from "./MapPropertiesServiceProps";
import MapPropertiesService from "./MapPropertiesService";

export interface IMapService {
    containsFeature(array: any[], bounds: google.maps.LatLngBounds): boolean,
    getBoundsRectangle(map: any): any,
    createPolygon(paths: any): google.maps.Polygon,
    createPolyline(path: any, weight: any): google.maps.Polyline,
    createShipPositionInformationObject(shipPosition: any, topmost?: any, highlight?: any): any,
    updateZoomByUrlParameter(map: any): void,
    getChangesWithinBounds(map: any, features: any): any,
    getFeaturesCount(map: any): number,
    unSelectFeatures(map: any, type: string): void,
    isPcsFeatureType(e: any): boolean,
    isSegmentFeatureType(e: any): boolean,
    isFairwayAreaFeatureType(e: any): boolean,
    fitFeatureBounds(map: any, feature: any): void,
    removeFeatures(map: any, property: string, value: any): void,
    processPoints(geometry: any, callback: any, thisArg: any): void,
    resetMapObj(obj: any): void,
    getFeatureGeometry(wkt: any): any,
    getGeometryBounds(geometry: any): google.maps.LatLngBounds,
    getGeometryPaths(geometry: any): google.maps.LatLng[][],
    getFeatureBounds(feature: any): google.maps.LatLngBounds,
    getFeaturePathBounds(feature: any): google.maps.LatLngBounds,
    addFeaturePathBounds(bounds: google.maps.LatLngBounds, feature: any): void,
    setFeatureGeometryAndBounds(feature: any): void,
    setFairwayAreaGeometryAndBounds(faiwayArea: any): void,
    featureOnMouseOverStyle(): any ,
    geometryFeatureStyle(feature: any, mouseover: boolean):any,
    fairwayAreaFeatureTypeStyle(feature: any): any,
    coordinatesToLatLng(coordinates: any): any,
    formatCoordinates(coordinates: any): any,
    getCoordinatesFromGeometry(geometry: any): any,
    extendBounds(bounds: google.maps.LatLngBounds, obj: any): void,
}

class MapService implements IMapService {

    _mapProperties: MapPropertiesProps;

    constructor() {
        this._mapProperties = MapPropertiesService.properties
    }

    containsFeature(array: any[], bounds: google.maps.LatLngBounds): boolean {
        let contains = false;
        for (const obj of array) {
            if (!Object.hasOwn(obj, "Eg")) continue;
            const latLngs = obj.Eg;
            for (const latLng of latLngs) {
                contains = bounds.contains(latLng);
                if (contains === true) break;
            }
        }

        return contains;
    }

    getBoundsRectangle(map:any): any {
        const defaultResult = {
            northEast: { lat: 0, lng: 0 },
            northWest: { lat: 0, lng: 0 },
            southWest: { lat: 0, lng: 0 },
            southEast: { lat: 0, lng: 0 }
        }

        if (typeof map.getBounds !== "function") return defaultResult;

        const bounds = map.getBounds();
        if (!bounds) return defaultResult;

        const ne = bounds.getNorthEast(); // Coords of the northeast corner
        const sw = bounds.getSouthWest(); // Coords of the southwest corner
        const nw = new window.google.maps.LatLng(ne.lat(), sw.lng()); // Coords of the NW corner
        const se = new window.google.maps.LatLng(sw.lat(), ne.lng()); // Coords of the SE corner

        return {
            northEast: { lat: ne.lat(), lng: ne.lng() },
            northWest: { lat: nw.lat(), lng: nw.lng() },
            southWest: { lat: sw.lat(), lng: sw.lng() },
            southEast: { lat: se.lat(), lng: se.lng() }
        };
    }

    createPolygon(paths: any): google.maps.Polygon {
        return new window.google.maps.Polygon({
            paths: paths,
            strokeColor: "#FF0000",
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: "Purple",
            fillOpacity: 0.35
        })
    }

    createPolyline(path:any, weight:any): google.maps.Polyline {
        return new window.google.maps.Polyline({
            path: path,
            geodesic: true,
            strokeColor: "#FF0000",
            strokeOpacity: 1.0,
            strokeWeight: weight
        });
    }

    createShipPositionInformationObject (shipPosition:any, topmost = false, highlight = false) {
        const position = new window.google.maps.LatLng(parseFloat(shipPosition.latitude), parseFloat(shipPosition.longitude));
        return {
            currentPilotageId: shipPosition.currentPilotageId,
            nextPilotageId: shipPosition.nextPilotageId,
            description: shipPosition.shipName,
            position: position,
            courseOverGround: shipPosition.courseOverGround,
            speedOverGround: shipPosition.speedOverGround,
            callSign: shipPosition.callSign,
            type: "ship",
            mmsi: shipPosition.mmsi,
            imoNo: shipPosition.imoNo,
            topmost: topmost,
            shipId: shipPosition.shipID,
            shipTypeGroupIdentifier: shipPosition.shipTypeGroupIdentifier,
            shipTypeIdentifier: shipPosition.shipTypeIdentifier,
            highlight: highlight
        };
    }

    updateZoomByUrlParameter (map:any) {
        const zoom = getNumericUrlParam("zoom");
        if (isNumeric(zoom) && zoom > 0) {
            map.setZoom(zoom);
        }
    }

    getChangesWithinBounds(map:any, features:any) {

        const bounds:any = map.getBounds();
        const segmentIds: number[] = [];
        const segmentGroupIds: number[] = [];

        features.forEach((feature:any) => {

            if (!bounds) return;
            const geometry = feature.getGeometry();

            if (!geometry) return;

            const array:any[] = geometry.getArray();
            if (isArrayEmpty(array)) return;
             
            if (this.containsFeature(array, bounds)) {
                const segmentId = feature.getProperty("segmentId");
                const segmentGroupId = feature.getProperty("segmentGroupId");

                if (isNumeric(segmentId) && segmentId > 0) {
                    if (!segmentIds.includes(segmentId)) {
                        segmentIds.push(segmentId);
                    }
                }

                if (isNumeric(segmentGroupId) && segmentGroupId > 0) {
                    if (!segmentGroupIds.includes(segmentGroupId)) {
                        segmentGroupIds.push(segmentGroupId);
                    }
                }
            } 
        });

        return {
            center: map.getCenter(),
            zoom: map.getZoom(),
            bounds: bounds,
            segmentIds: segmentIds,
            segmentGroupIds: segmentGroupIds,
        };
    }

    getFeaturesCount(map:any):number {
        let count = 0;
        map.data.forEach(function () {
            count += 1;
        });

        return count;
    }

    unSelectFeatures(map:any, type:string) {
        map.data.forEach((feature:any) => {
            const featureType = feature.getProperty("featureType");
            if (featureType === type) {
                const isSelected = feature.getProperty("isSelected");

                if (!isSelected) return;
                map.data.overrideStyle(feature,
                    {
                        fillColor: this._mapProperties.DefaultFillColor,
                        fillOpacity: this._mapProperties.DefaultOpacity,
                        strokeWeight: 2,
                        strokeColor: this._mapProperties.DefaultStrokeColor
                    }
                );
                feature.setProperty("isSelected", false);
            }
        });
    }

    isPcsFeatureType = (e:any):boolean => {
        return e.getProperty("featureType") === this._mapProperties.PcsFeatureType;
    }

    isSegmentFeatureType (e:any):boolean {
        return e.getProperty("featureType") === this._mapProperties.SegmentFeatureType;
    }

    isFairwayAreaFeatureType(e:any):boolean {
        return e.getProperty("featureType") === this._mapProperties.FairwayAreaFeatureType;
    }

    fitFeatureBounds(map: any, feature: any) {
        const bounds = new window.google.maps.LatLngBounds();
        this.processPoints(feature.getGeometry(), bounds.extend, bounds);
        map.fitBounds(bounds);
    }

    removeFeatures(map: any, property: string, value: any) {
        map.data.forEach((e:any) => {
            if (e.getProperty(property) === value) {
                map.data.remove(e);
            }
        });
    }

    processPoints(geometry: any, callback: any, thisArg: any) {
        if (geometry instanceof window.google.maps.LatLng) {
            callback.call(thisArg, geometry);
        } else if (geometry instanceof window.google.maps.Data.Point) {
            callback.call(thisArg, geometry.get());
        } else {
            geometry.getArray().forEach((g: any) => {
                this.processPoints(g, callback, thisArg);
            });
        }
    }

    resetMapObj(obj: any) {
        if (isObjectNull(obj)) return;
        obj.setMap(null);
    }

    getFeatureGeometry(wkt: any): any {
        return wktToGeoJSON(wkt);
    }

    getGeometryBounds(geometry: any): google.maps.LatLngBounds {
        const bounds = new google.maps.LatLngBounds();
        for (const coords of geometry.coordinates) {
            for (const coord of coords) {
                const latLng = new google.maps.LatLng(coord[1], coord[0]);
                bounds.extend(latLng);
            }
        }
        return bounds;
    }

    getGeometryPaths(geometry: any): google.maps.LatLng[][] {
        switch (geometry.type) {
            case "MultiPolygon":
                return this.getMultiPolygonGeometryPaths(geometry);
            default:
                return this.getPolygonGeometryPaths(geometry);
        }
    }

    getPolygonGeometryPaths(geometry: any): google.maps.LatLng[][] {
        const paths: google.maps.LatLng[][] = [];
        for (const coords of geometry.coordinates) {
            const subPaths: any[] = [];
            for (const coord of coords) {
                subPaths.push({ lat: coord[1], lng: coord[0] });
            }
            paths.push(subPaths);
        }
        return paths;
    }

    getMultiPolygonGeometryPaths(geometry: any): google.maps.LatLng[][] {
        const paths: google.maps.LatLng[][] = [];
        for (const multiPolygon of geometry.coordinates) {
            for (const ring of multiPolygon) {
                const path: any[] = [];
                for (const coord of ring) {
                    path.push({ lat: coord[1], lng: coord[0] });
                }
                paths.push(path);
            }
        }
        return paths;
    }

    getFeatureBounds(feature: any):google.maps.LatLngBounds {
        const bounds = new google.maps.LatLngBounds();
        //iterate over the paths
        feature.getGeometry().getArray().forEach((path:any) => {
            path.getArray().forEach((latLng: google.maps.LatLng) => {
                bounds.extend(latLng);
            });

        });
        return bounds;
    }

    getFeaturePathBounds(feature: any):google.maps.LatLngBounds {
        const bounds = new google.maps.LatLngBounds();
        for (const paths of feature.paths) {
            for (const path of paths) {
                const latLng = new google.maps.LatLng(path.lat, path.lng);
                bounds.extend(latLng);
            }
        }
        return bounds;
    }

    addFeaturePathBounds(bounds: google.maps.LatLngBounds, feature: any) {
        for (const paths of feature.paths) {
            for (const path of paths) {
                const latLng = new google.maps.LatLng(path.lat, path.lng);
                bounds.extend(latLng);
            }
        }
    }

    setFeatureGeometryAndBounds(feature: any) {

        if (feature.geometry) return;
        
        const geometry = this.getFeatureGeometry(feature.properties.wkt);
        const geometryBounds = this.getGeometryBounds(geometry);
        const paths = this.getGeometryPaths(geometry);


        feature.geometry = geometry;
        feature.geometryBounds = geometryBounds;
        feature.paths = paths;
    }

    setFairwayAreaGeometryAndBounds(fairwayArea: any) {

        const geometry = fairwayArea.geometry ?? this.getFeatureGeometry(fairwayArea.wkt);
        const geometryBounds = this.getGeometryBounds(geometry);
        const paths = this.getGeometryPaths(geometry);


        fairwayArea.geometry = geometry;
        fairwayArea.geometryBounds = geometryBounds;
        fairwayArea.paths = paths;
    }

    featureOnMouseOverStyle(): any {
        return {
            strokeColor: this._mapProperties.MouseOverStrokeColor,
            fillColor: this._mapProperties.MouseOverStrokeColor,
            strokeWeight: 4,
            zIndex: 1000
        }
    }

    geometryFeatureStyle(feature:any, mouseover = false):any { 
        const isSelected = feature.getProperty("isSelected");
        return {
            fillColor: isSelected ? this._mapProperties.SelectedFillColor : mouseover ? this._mapProperties.MouseOverStrokeColor : this._mapProperties.DefaultFillColor,
            fillOpacity: this._mapProperties.DefaultOpacity,
            strokeWeight: 2,
            strokeColor: isSelected ? this._mapProperties.SelectedStrokeColor : mouseover ? this._mapProperties.MouseOverStrokeColor : this._mapProperties.DefaultStrokeColor
        }
    }

    fairwayAreaFeatureTypeStyle(feature:any):any {
        const isApplied = feature.getProperty('isApplied');
        const polygonColor = isApplied ? this._mapProperties.ColorFairwayAreaApplied : this._mapProperties.ColorFairwayArea;
        return {
            strokeColor: isApplied ? polygonColor : this._mapProperties.ColorFairwayAreaStroke,
            strokeOpacity: 0.9,
            strokeWeight: 3,
            fillColor: polygonColor,
            fillOpacity: 0.5,
            zIndex: isApplied ? 2 : 1
        }
    }

    coordinatesToLatLng(coordinates: any) {
        const result = [];
        for (const coordinate of coordinates) {
            const latLng = { lat: Number(coordinate[1]), lng: Number(coordinate[0]) };
            result.push(latLng);
        }
        return result;
    }

    formatCoordinates(coordinates: any) {
        const result = [];
        for (const coordinate of coordinates) {
            if (coordinate.length === 1) {
                const latLngs = this.coordinatesToLatLng(coordinate[0]);
                result.push(latLngs);
            } else {
                for (const subCoordinate of coordinate) {
                    result.push(this.coordinatesToLatLng(subCoordinate));
                }
            }
        }
        return result;
    }

    getCoordinatesFromGeometry(geometry: any) {
        let coordinates = [];
        if (geometry.type === "Polygon") {
            coordinates = this.coordinatesToLatLng(geometry.coordinates[0]);
        } else {
            coordinates = this.formatCoordinates(geometry.coordinates);
        }
        return coordinates;
    }

    extendBounds(bounds: google.maps.LatLngBounds, obj: any) {
        if (Array.isArray(obj)) {
            for (const item of obj) {
                this.extendBounds(bounds, item);
            }
        } else {
            bounds.extend(obj);
        }
    }
}

export default MapService;


